// ── Controller Performance Upgrades — Data ────────────────────────────────────
// Single source of truth for the Performance Upgrades page.
// All pricing is null by default — intended to be pulled from the Google Sheet
// or admin panel in a future CMS pass. Never hardcode prices here.
//
// displayOrder controls section / card ordering.
// Set isActive: false to hide a section or variant without deleting it.

export interface UpgradeMedia {
  heroImage: string | null;
  photos: string[];
  videoFile: string | null;     // local /public file
  youtubeUrl: string | null;
  tiktokUrl: string | null;
}

export interface UpgradeVariant {
  id: string;
  name: string;
  tagline: string;
  description: string;
  benefits: string[];
  price: number | null;         // null = show "Get a Quote" / pull from sheet
  pricePrefix: string | null;   // e.g. "Starting at"
  isRecommended: boolean;
  isActive: boolean;
  media: UpgradeMedia;
  displayOrder: number;
}

export interface ComparisonCell {
  value: boolean | string;      // true → ✓, false → ✗, string → text label
}

export interface ComparisonRow {
  feature: string;
  cells: ComparisonCell[];      // one entry per variant, same order
}

export interface UpgradeSection {
  id: string;                   // used as scroll anchor id
  navLabel: string;             // short label shown in sticky nav
  title: string;
  subtitle: string;
  description: string;
  variants: UpgradeVariant[];
  comparisonHeaders: string[] | null;   // null = no table for this section
  comparisonRows: ComparisonRow[] | null;
  ctaLabel: string;
  ctaHref: string;
  isActive: boolean;
  displayOrder: number;
}

// ─────────────────────────────────────────────────────────────────────────────

const BLANK_MEDIA: UpgradeMedia = {
  heroImage: null,
  photos: [],
  videoFile: null,
  youtubeUrl: null,
  tiktokUrl: null,
};

export const UPGRADE_SECTIONS: UpgradeSection[] = [

  // ── Section 1: TMR Magnetic Joysticks ──────────────────────────────────────
  {
    id: 'tmr-joysticks',
    navLabel: 'TMR Joysticks',
    title: 'TMR Magnetic Joysticks',
    subtitle: 'The single best upgrade you can make to any controller.',
    description:
      'Traditional potentiometer joysticks wear out — the carbon wiper degrades with use, causing the dreaded stick drift. TMR (Tunneling Magneto-Resistance) joysticks use a magnetic field instead of physical contact. Nothing touches, nothing wears, nothing drifts.',
    ctaLabel: 'Add TMR Upgrade',
    ctaHref: '/book-repair',
    isActive: true,
    displayOrder: 1,
    comparisonHeaders: ['Feature', 'K-Silver TMR', 'GuliKit TMR'],
    comparisonRows: [
      { feature: 'Drift-proof technology',     cells: [{ value: true },  { value: true }] },
      { feature: 'Adjustable joystick tension', cells: [{ value: false }, { value: true }] },
      { feature: 'Interchangeable stick heights',cells: [{ value: false }, { value: '3 heights' }] },
      { feature: 'Drop-in replacement',         cells: [{ value: true },  { value: true }] },
      { feature: 'Best for',                    cells: [{ value: 'Most players' }, { value: 'Competitive / tuners' }] },
      { feature: 'Price',                       cells: [{ value: 'Get a quote' }, { value: 'Get a quote' }] },
    ],
    variants: [
      {
        id: 'ksilver-tmr',
        name: 'K-Silver TMR',
        tagline: 'Drift-proof. Reliable. Great value.',
        description:
          'K-Silver TMR joysticks use the same magnetic hall-effect technology as premium options at a more accessible price point. A drop-in replacement for worn potentiometers — immediately eliminates drift.',
        benefits: [
          'Drift-resistant magnetic sensing — no physical contact',
          'Smooth, consistent movement across the full range',
          'Direct replacement for DualSense and Xbox potentiometers',
          'Excellent value for most players',
        ],
        price: null,
        pricePrefix: 'Starting at',
        isRecommended: false,
        isActive: true,
        displayOrder: 1,
        media: {
          ...BLANK_MEDIA,
        },
      },
      {
        id: 'gulikit-tmr',
        name: 'GuliKit TMR',
        tagline: 'Precision-tunable. The professional choice.',
        description:
          'GuliKit TMR joysticks bring the highest level of customisation available — adjustable tension lets you dial in resistance to match your grip strength, and three swappable stick heights let you fine-tune reach.',
        benefits: [
          'Drift-proof magnetic TMR technology',
          'Adjustable spring tension — tune resistance to your preference',
          'Three interchangeable stick heights included',
          'Preferred by competitive players and content creators',
        ],
        price: null,
        pricePrefix: 'Starting at',
        isRecommended: true,
        isActive: true,
        displayOrder: 2,
        media: {
          ...BLANK_MEDIA,
        },
      },
    ],
  },

  // ── Section 2: Mouse Click Upgrades ────────────────────────────────────────
  {
    id: 'mouse-click',
    navLabel: 'Mouse Click',
    title: 'Mouse Click Upgrades',
    subtitle: 'Shorter travel. Instant response. Competitive advantage.',
    description:
      'Standard controller buttons use rubber dome contacts that compress before registering. Mouse click upgrades replace those with tactile mechanical switches — the same technology in high-end gaming mice — for a crisp, audible click and near-zero actuation travel.',
    ctaLabel: 'Add Click Upgrades',
    ctaHref: '/book-repair',
    isActive: true,
    displayOrder: 2,
    comparisonHeaders: null,
    comparisonRows: null,
    variants: [
      {
        id: 'click-triggers',
        name: 'Mouse Click Triggers',
        tagline: 'Instant LT / RT actuation.',
        description:
          'Replaces the analog trigger mechanism with a crisp mechanical switch. Full pull becomes a definitive click — no guesswork, no partial inputs. Ideal for shooters where trigger speed matters.',
        benefits: [
          'Instant trigger actuation — no travel dead zone',
          'Tactile click feedback on every shot',
          'No accidental half-pulls in competitive play',
          'Compatible with DualSense and Xbox controllers',
        ],
        price: null,
        pricePrefix: null,
        isRecommended: false,
        isActive: true,
        displayOrder: 1,
        media: {
          ...BLANK_MEDIA,
          videoFile: '/clicky-triggers-buttons.mp4',
        },
      },
      {
        id: 'click-bumpers',
        name: 'Mouse Click Bumpers',
        tagline: 'Crisp LB / RB response.',
        description:
          'The bumpers (LB/RB) are often the first buttons to develop mushiness. A mechanical switch replacement gives each press an unmistakable click and dramatically faster reset between inputs.',
        benefits: [
          'Mechanical switch bumper replacement',
          'Tactile, audible confirmation on every press',
          'Faster reset between repeated bumper inputs',
          'Great for ability-heavy games and movement techniques',
        ],
        price: null,
        pricePrefix: null,
        isRecommended: false,
        isActive: true,
        displayOrder: 2,
        media: {
          ...BLANK_MEDIA,
          videoFile: '/clicky-triggers-buttons.mp4',
        },
      },
      {
        id: 'click-face-dpad',
        name: 'Mouse Click Face Buttons & D-Pad',
        tagline: 'Every press confirmed.',
        description:
          'Mechanical switch upgrades for ABXY and the directional pad. Combined into one installation, this turns every face button and d-pad input into a precise, satisfying click rather than a soft dome press.',
        benefits: [
          'Mechanical switches on all face buttons (A/B/X/Y or Cross/Circle/Square/Triangle)',
          'D-pad switches for clean directional inputs',
          'No more mushy, uncertain presses',
          'Pairs perfectly with trigger and bumper upgrades',
        ],
        price: null,
        pricePrefix: null,
        isRecommended: true,
        isActive: true,
        displayOrder: 3,
        media: {
          ...BLANK_MEDIA,
          videoFile: '/clicky-triggers-buttons.mp4',
        },
      },
    ],
  },

  // ── Section 3: Back Paddle Systems ─────────────────────────────────────────
  {
    id: 'back-paddles',
    navLabel: 'Back Paddles',
    title: 'Back Paddle Systems',
    subtitle: 'Keep your thumbs on the sticks. Always.',
    description:
      'Back paddles let you map any button to a lever under your middle or ring fingers — so you never have to lift your thumb off the joystick to jump, reload, or switch weapons. Three tiers to match how seriously you play.',
    ctaLabel: 'Add Back Paddles',
    ctaHref: '/book-repair',
    isActive: true,
    displayOrder: 3,
    comparisonHeaders: ['Feature', 'Rise4', 'Rise4 Plus Max', 'Spark Kit'],
    comparisonRows: [
      { feature: 'Back paddles',              cells: [{ value: true },  { value: true },  { value: true }] },
      { feature: 'Adaptive trigger switch',    cells: [{ value: false }, { value: true },  { value: true }] },
      { feature: 'Regular trigger mode',       cells: [{ value: true },  { value: true },  { value: true }] },
      { feature: 'Mouse click trigger mode',   cells: [{ value: false }, { value: true },  { value: true }] },
      { feature: 'Built-in LED screen',         cells: [{ value: false }, { value: false }, { value: true }] },
      { feature: 'Price',                      cells: [{ value: 'Get a quote' }, { value: 'Get a quote' }, { value: 'Get a quote' }] },
    ],
    variants: [
      {
        id: 'rise4',
        name: 'Rise4',
        tagline: 'The entry-point to paddle gaming.',
        description:
          'Rise4 adds two programmable paddles to the back of your controller. A clean, reliable upgrade that keeps your thumbs on the sticks for jumping, reloading, and weapon swaps.',
        benefits: [
          'Two programmable paddles on the rear',
          'Remap any face button to a paddle',
          'Comfortable ergonomic placement',
          'No modification to trigger function',
        ],
        price: null,
        pricePrefix: 'Starting at',
        isRecommended: false,
        isActive: true,
        displayOrder: 1,
        media: {
          ...BLANK_MEDIA,
          videoFile: '/rise4 back paddles.mp4',
        },
      },
      {
        id: 'rise4-plus-max',
        name: 'Rise4 Plus Max',
        tagline: 'Paddles + adaptive trigger switching.',
        description:
          'Rise4 Plus Max adds adaptive trigger switching on top of the standard paddle setup — flip between regular analog trigger feel and instant mouse click actuation on the fly without opening anything.',
        benefits: [
          'All Rise4 paddle features included',
          'Switch between regular and mouse click trigger modes',
          'Adaptive trigger switching — no tools required',
          'Best all-around upgrade for competitive players',
        ],
        price: null,
        pricePrefix: 'Starting at',
        isRecommended: true,
        isActive: true,
        displayOrder: 2,
        media: {
          ...BLANK_MEDIA,
          videoFile: '/rise4+max-adapativetriggers.mp4',
        },
      },
      {
        id: 'spark-kit',
        name: 'Spark Kit',
        tagline: 'The premium paddle experience.',
        description:
          'The Spark Kit is a full-featured paddle system with adaptive trigger switching, mouse click trigger capability, and a built-in LED screen for profile management — a feature the Rise4 and Rise4 Plus Max do not have.',
        benefits: [
          'Built-in LED screen for profile management',
          'Adaptive trigger switching',
          'Regular and mouse click trigger modes',
          'Full paddle remapping',
        ],
        price: null,
        pricePrefix: 'Starting at',
        isRecommended: false,
        isActive: true,
        displayOrder: 3,
        media: {
          ...BLANK_MEDIA,
        },
      },
    ],
  },

  // ── Section 4: Rear Action Buttons ─────────────────────────────────────────
  {
    id: 'rear-buttons',
    navLabel: 'Rear Buttons',
    title: 'Rear Action Buttons',
    subtitle: "Not everyone likes paddles. Here's the alternative.",
    description:
      'Back paddles extend below the controller and are felt against your middle fingers. Rear action buttons are flush-mounted on the back panel — smaller, less intrusive, and preferred by players who want extra inputs without the bulk.',
    ctaLabel: 'Discuss My Build',
    ctaHref: '/book-repair',
    isActive: true,
    displayOrder: 4,
    comparisonHeaders: ['Feature', 'Back Paddles', 'Rear Action Buttons'],
    comparisonRows: [
      { feature: 'Extra inputs',            cells: [{ value: '2–4 paddles' }, { value: 'Up to 4 buttons' }] },
      { feature: 'Profile',                 cells: [{ value: 'Extended below grip' }, { value: 'Flush with back panel' }] },
      { feature: 'Feel during play',        cells: [{ value: 'Lever-style under finger' }, { value: 'Button press on back' }] },
      { feature: 'Bulkier than stock',      cells: [{ value: true },  { value: false }] },
      { feature: 'Better for small hands',  cells: [{ value: false }, { value: true }] },
      { feature: 'Best for',               cells: [{ value: 'Most competitive players' }, { value: 'Comfort-focused players' }] },
    ],
    variants: [
      {
        id: 'back-paddles-vs',
        name: 'Back Paddles',
        tagline: 'Lever-style inputs below the grip.',
        description:
          'Paddles extend below the controller body and are actuated by curling your middle or ring finger. The most common competitive upgrade — proven in tournament play.',
        benefits: [
          'Most natural motion for fast inputs',
          'Widely used in competitive play',
          'Up to 4 programmable paddles',
          'Multiple tier options (Rise4, Rise4 Max, Spark)',
        ],
        price: null,
        pricePrefix: null,
        isRecommended: false,
        isActive: true,
        displayOrder: 1,
        media: { ...BLANK_MEDIA, photos: ['/img_3248.jpeg'] },
      },
      {
        id: 'rear-action-buttons',
        name: 'Rear Action Buttons',
        tagline: 'Flush, low-profile extra inputs.',
        description:
          'Rear action buttons sit flush on the back of the controller — pressed by the same fingers used for paddles, but with a button feel rather than a lever. Lower profile, more comfortable for some grip styles, and available with up to four programmable positions.',
        benefits: [
          'Lower profile — no extensions below the grip',
          'Up to four programmable rear buttons',
          'Better for players who find paddles uncomfortable',
          'Cleaner look than extended paddle systems',
        ],
        price: null,
        pricePrefix: null,
        isRecommended: false,
        isActive: true,
        displayOrder: 2,
        media: { ...BLANK_MEDIA },
      },
    ],
  },

  // ── Section 5: Shell & Grip Options ────────────────────────────────────────
  {
    id: 'shell-grip',
    navLabel: 'Shell & Grip',
    title: 'Shell & Grip Options',
    subtitle: 'Make it yours. Inside and out.',
    description:
      'Performance starts with how the controller feels in your hands. A custom shell, tactical grip surface, or LED accent kit transforms your controller from a generic peripheral into something built specifically for you.',
    ctaLabel: 'Start My Build',
    ctaHref: '/custom-controllers',
    isActive: true,
    displayOrder: 5,
    comparisonHeaders: null,
    comparisonRows: null,
    variants: [
      {
        id: 'custom-shell',
        name: 'Custom Shell',
        tagline: 'Any colour. Any graphic. Your design.',
        description:
          'Full shell swap — front, back, and buttons. Choose from dozens of solid colours, chrome finishes, or bring a graphic and we will apply it. All custom builds include TMR joysticks as standard.',
        benefits: [
          'Complete front and back shell replacement',
          'Dozens of colour and finish options',
          'Graphic wraps and printed designs available',
          'TMR joysticks included in all custom shell builds',
        ],
        price: null,
        pricePrefix: 'Starting at',
        isRecommended: false,
        isActive: true,
        displayOrder: 1,
        media: {
          ...BLANK_MEDIA,
          photos: [
            '/dualsensecustomgraphic-spider.jpg',
            '/dualsensecustomgraphic-golddragon.jpg',
            '/dualsensecustomgraphic-lightning.jpg',
            '/dualsensecustomgraphic-cherryblossom.jpg',
          ],
        },
      },
      {
        id: 'transparent-shell',
        name: 'Transparent Shell',
        tagline: 'Show off what is inside.',
        description:
          'A clear shell exposes the internal components — and pairs exceptionally well with the LED button kit below for an eye-catching glow effect. Popular with streamers and collectors.',
        benefits: [
          'Crystal-clear or smoked transparent finish',
          'Pairs perfectly with LED button kits',
          'Favourite among streamers and display builds',
          'Available in multiple tint levels',
        ],
        price: null,
        pricePrefix: 'Starting at',
        isRecommended: false,
        isActive: true,
        displayOrder: 2,
        media: { ...BLANK_MEDIA },
      },
      {
        id: 'led-buttons',
        name: 'LED Button Kit',
        tagline: 'Glow-through face buttons and accents.',
        description:
          'Illuminated face buttons, d-pad, and accent lighting visible through the front shell or a transparent shell. Controllable lighting that makes your build impossible to miss.',
        benefits: [
          'Illuminated face buttons and directional pad',
          'Accent lighting through shell vents and seams',
          'Best paired with transparent shell',
          'Unique visual impact for streaming setups',
        ],
        price: null,
        pricePrefix: 'Starting at',
        isRecommended: false,
        isActive: true,
        displayOrder: 3,
        media: { ...BLANK_MEDIA },
      },
      {
        id: 'grip-rear-shell',
        name: 'Grip Rear Shell',
        tagline: 'Better control. Less fatigue.',
        description:
          'A textured grip rear shell replaces the stock smooth plastic with a tactile rubberised or textured surface. During long sessions, this small change significantly reduces hand fatigue and grip slippage.',
        benefits: [
          'Textured surface for better grip and control',
          'Reduces hand fatigue during long sessions',
          'No slippage — even during sweaty play',
          'Available in multiple finishes to match your build',
        ],
        price: null,
        pricePrefix: 'Starting at',
        isRecommended: false,
        isActive: true,
        displayOrder: 4,
        media: { ...BLANK_MEDIA },
      },
    ],
  },

];

// ── Build Inspiration gallery images ──────────────────────────────────────────
// Pulled directly from existing gallery assets.
export const INSPIRATION_GALLERY = [
  { src: '/dualsensecustomgraphic-spider.jpg',        label: 'Spider Build'       },
  { src: '/dualsensecustomgraphic-golddragon.jpg',   label: 'Gold Dragon'        },
  { src: '/dualsensecustomgraphic-lightning.jpg',    label: 'Lightning'          },
  { src: '/dualsensecustomgraphic-cherryblossom.jpg', label: 'Cherry Blossom'    },
  { src: '/dualsensecustomgraphic-milesmorales.jpg', label: 'Miles Morales'      },
  { src: '/dualsensecustomgraphic-vangogh.jpg',      label: 'Van Gogh'           },
  { src: '/dualsensecustomgraphic-skull.jpg',        label: 'Skull'              },
  { src: '/cammo.JPEG',                              label: 'Camo'               },
  { src: '/forest_green_.JPEG',                      label: 'Forest Green'       },
  { src: '/matte_red,_gold_trim.JPEG',               label: 'Matte Red / Gold'   },
  { src: '/wood_grain.JPEG',                         label: 'Wood Grain'         },
  { src: '/powder_blue.JPEG',                        label: 'Powder Blue'        },
  { src: '/yellow.JPEG',                             label: 'Yellow'             },
];
